<template>
  <div class="c-partner-segment-filter-select">
    <BittsPopover
      v-model:open="isOpen"
      placement="bottomLeft"
      :mount-to-body="true"
    >
      <div
        role="combobox"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        :tabindex="0"
        class="main-select"
        :class="{
          open: isOpen,
          disabled: props.disabled,
        }"
        @click="openDropdown"
        @keydown.enter="openDropdown"
        @keydown.space.prevent="openDropdown"
      >
        <div
          v-if="!hasSelections"
          class="c-partner-segment-filter-select__placeholder"
        >
          Select partner populations
        </div>

        <div
          v-else
          ref="selectedItemsContainer"
          class="c-partner-segment-filter-select__selected-items"
        >
          <BittsTag
            v-for="(chip, index) in chips"
            :key="chip.key"
            :ref="(el) => setTagRef(el, index)"
            color="neutral"
            size="x-small"
            class="c-partner-segment-filter-select__tag"
            :class="{
              'c-partner-segment-filter-select__tag--hidden':
                index >= visibleCount,
            }"
            @click.stop="!props.disabled && removeChip(chip)"
          >
            <BittsTooltip
              placement="topLeft"
              :mount-to-body="true"
              :show-tooltip="
                resolvedSegments.some(
                  (s) => s.type === chip.segment && s.populations.length,
                )
                  ? null
                  : false
              "
            >
              <div class="inline-flex items-center gap-4 text-sm">
                <span>{{ chip.label }}</span>
                <FontAwesomeIcon :icon="['fak', 'x']" />
              </div>
              <template #title>
                <AccountMappingFiltersPopulationsHover
                  :show-segment-header="false"
                  :resolved-segments="
                    resolvedSegments.filter((s) => s.type === chip.segment)
                  "
                />
              </template>
            </BittsTooltip>
          </BittsTag>

          <BittsTooltip v-if="overflowCount > 0" :mount-to-body="true">
            <BittsTag
              ref="overflowTag"
              size="x-small"
              color="neutral"
              class="c-partner-segment-filter-select__overflow-tag"
            >
              +{{ overflowCount }}
            </BittsTag>
            <template #title>
              <div
                class="gap-4 c-partner-segment-filter-select__tooltip-scroll"
              >
                <div
                  v-for="chip in overflowChips"
                  :key="chip.key"
                  class="flex flex-col gap-2"
                >
                  <span class="font-semibold">{{ chip.label }}</span>
                  <span
                    v-for="pop in chip.populations"
                    :key="pop"
                    class="pl-8"
                    >{{ pop }}</span
                  >
                </div>
              </div>
            </template>
          </BittsTooltip>
        </div>

        <div class="controls">
          <FontAwesomeIcon
            :icon="['fak', isOpen ? 'chevron-up' : 'chevron-down']"
            class="text-neutral-text-placeholder cursor-pointer"
          />
        </div>
      </div>

      <template #content>
        <div v-if="isOpen" class="w-full z-50">
          <PartnerPopulationFilterTree
            :selected-filters="model"
            :show-search="true"
            @update="handleUpdate"
          />
        </div>
      </template>
    </BittsPopover>
  </div>
</template>

<script setup lang="ts">
import { BittsPopover, BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';

import AccountMappingFiltersPopulationsHover, {
  type ResolvedPopulationSegmentType,
} from '@/components/reports/AccountMappingFiltersPopulationsHover.vue';
import PartnerPopulationFilterTree from '@/components/reports/PartnerPopulationFilterTree.vue';

import { useChipOverflow } from '@/composables/reports/useChipOverflow';
import { usePartnersStore, usePopulationsStore } from '@/stores';
import {
  ALL_SEGMENTS,
  SEGMENTS_TO_LABEL_MAP,
  SegmentType,
} from '@/types/populations';
import { ReportPartnerSegmentFilters } from '@/types/reports';

type Chip = {
  key: string;
  label: string;
  populations: string[];
  segment: string;
};

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const model = defineModel<ReportPartnerSegmentFilters>({
  default: () => ({ items: [] }),
});

const popsStore = usePopulationsStore();
const partnersStore = usePartnersStore();

const isOpen = ref(false);

const resolvedSegments = computed<ResolvedPopulationSegmentType[]>(() => {
  const items = model.value?.items ?? [];
  if (!items.length) return [];
  return ALL_SEGMENTS.flatMap((segmentType) => {
    const segmentItems = items.filter((item) => item.segment === segmentType);
    if (!segmentItems.length) return [];
    const allPops = popsStore.getPartnerPopulationsBySegment(segmentType);
    const filterIds = segmentItems.every(
      (f) => 'population_ids' in f && f.population_ids?.length,
    )
      ? segmentItems.flatMap((f) =>
          'population_ids' in f ? (f.population_ids ?? []) : [],
        )
      : undefined;
    const populations = allPops
      .filter((pop) => !filterIds || filterIds.includes(pop.id))
      .map((pop) => ({
        id: pop.id,
        name: pop.name,
        partnerOrg: partnersStore.getPartnerOrgById(pop.organization_id ?? 0),
      }));
    return populations.length
      ? [
          {
            type: segmentType,
            label: SEGMENTS_TO_LABEL_MAP[segmentType],
            populations,
          },
        ]
      : [];
  });
});

const chips = computed<Chip[]>(() => {
  const bySegment = new Map<string, string[]>();

  for (const item of model.value?.items ?? []) {
    if (!('organization_id' in item) || !item.organization_id) {
      const allPops = popsStore.getPartnerPopulationsBySegment(
        item.segment as SegmentType,
      );
      const populations = allPops.map((p) => {
        const partnerName =
          partnersStore.getPartnerOrgById(p.organization_id)?.name ??
          `Partner ${p.organization_id}`;
        return `${p.name} (${partnerName})`;
      });
      const existing = bySegment.get(item.segment) ?? [];
      bySegment.set(item.segment, [...existing, ...populations]);
      continue;
    }

    const partnerName =
      partnersStore.getPartnerOrgById(item.organization_id)?.name ??
      `Partner ${item.organization_id}`;

    const allPops = popsStore
      .getPartnerPopulationsBySegment(item.segment as SegmentType)
      .filter((p) => p.organization_id === item.organization_id);

    const populations = item.population_ids?.length
      ? item.population_ids
          .map((id) => allPops.find((p) => p.id === id)?.name)
          .filter((n): n is string => !!n)
          .map((name) => `${name} (${partnerName})`)
      : allPops.map((p) => `${p.name} (${partnerName})`);

    const existing = bySegment.get(item.segment) ?? [];
    bySegment.set(item.segment, [...existing, ...populations]);
  }

  return [...bySegment.entries()]
    .map(([segment, populations]) => {
      const segmentLabel =
        SEGMENTS_TO_LABEL_MAP[segment as SegmentType] ?? segment;
      return {
        key: segment,
        label: `${segmentLabel} (${populations.length})`,
        populations,
        segment,
      };
    })
    .filter((chip) => chip.populations.length > 0);
});

const hasSelections = computed(() => chips.value.length > 0);

const { selectedItemsContainer, overflowTag, visibleCount, setTagRef } =
  useChipOverflow(chips);

const overflowChips = computed(() => chips.value.slice(visibleCount.value));
const overflowCount = computed(() => overflowChips.value.length);

function openDropdown() {
  if (props.disabled) return;
  isOpen.value = true;
}

function handleUpdate(val: ReportPartnerSegmentFilters) {
  model.value = val;
}

function removeChip(chip: Chip) {
  model.value = {
    ...model.value,
    items: model.value.items.filter((item) => item.segment !== chip.segment),
  };
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.c-partner-segment-filter-select {
  @apply flex-1 min-w-[120px];
}

.main-select {
  @apply flex items-center justify-between gap-4 px-8 py-4
    border border-neutral-border rounded-r-lg bg-white cursor-pointer
    min-h-[32px] min-w-0;

  &:hover:not(.disabled),
  &.open {
    @apply border-info-accent outline-none z-10 relative;
  }

  &.disabled {
    @apply opacity-60 cursor-default;
  }
}

.c-partner-segment-filter-select__placeholder {
  @apply text-neutral-text-placeholder text-sm truncate;
}

.c-partner-segment-filter-select__selected-items {
  @apply flex-1 flex flex-nowrap gap-4 overflow-hidden;
}

.c-partner-segment-filter-select__tag {
  @apply inline-flex items-center gap-4 shrink-0 cursor-pointer pointer-events-auto;
}

.c-partner-segment-filter-select__tag--hidden {
  @apply hidden;
}

.c-partner-segment-filter-select__overflow-tag {
  @apply inline-flex items-center shrink-0;
}

.c-partner-segment-filter-select__tooltip-scroll {
  @apply max-h-[200px] overflow-y-auto flex flex-col;
  scrollbar-color: rgba(255, 255, 255, 0.4) transparent;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.4);
    border-radius: 9999px;
  }
}

.controls {
  @apply flex items-center shrink-0 ml-auto;
}
</style>
